<template>
  <FadeTransition v-if="contract">
    <SettingsPagePanel>
      <SettingsPagePanelHeader>
        <template #title>{{ title }}</template>
        <template #right>
          <div v-if="isEditing" :class="$style.buttonGroup">
            <FlatButton
              variant="secondary"
              size="medium"
              @click="handleCancelButtonClick"
            >
              キャンセル
            </FlatButton>
            <FlatButton
              variant="primary"
              size="medium"
              @click="handleButtonClick"
              :disabled="isButtonDisabled"
            >
              保存
            </FlatButton>
          </div>
          <div v-else>
            <FlatButton
              variant="primary"
              size="medium"
              @click="handleButtonClick"
              :disabled="isButtonDisabled"
            >
              <template #icon>
                <IconPen />
              </template>
              編集
            </FlatButton>
          </div>
        </template>
      </SettingsPagePanelHeader>
      <section :class="$style.content">
        <CautionAlert>
          請求先情報・カード情報を更新した場合、今月利用分（次月請求分）から保存した情報が利用されます。請求先情報の変更可能期間は、<strong>毎月10日から月末まで</strong>となっております。
        </CautionAlert>
        <SettingsTable :isEditing="isEditing">
          <SettingsTableRow name="請求先名">
            <template #afterName>
              <HelpPoptip :class="$style.poptip">
                <p :class="$style.poptip__tips">
                  請求書に記載されるお名前です。
                </p>
              </HelpPoptip>
            </template>
            <p>{{ contract.invoiceCustomerName }}</p>
            <template #editor>
              <p :class="$style.name">
                <DolphinTextInput v-model="formValues.invoiceCustomerName" />
                {{ toTitle }}
              </p>
            </template>
          </SettingsTableRow>

          <SettingsTableRow name="請求先メールアドレス">
            <p>{{ contract.email }}</p>
            <template #editor
              ><DolphinTextInput v-model="formValues.email" />
            </template>
          </SettingsTableRow>

          <ContractEditorCreditCardRow
            :isEditablePeriod="contract.isEditablePeriod"
          />
        </SettingsTable>
      </section>
    </SettingsPagePanel>
  </FadeTransition>
</template>

<script setup lang="ts">
import IconPen from '@/components/icons/IconPen.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import HelpPoptip from '@/components/molecules/HelpPoptip.vue'
import FadeTransition from '@/components/transitions/FadeTransition.vue'
import { useUpdateContract } from '@/features/contract/useContract/useContract'
import router from '@/router'
import { ContractType, PaymentMethod } from '@/types/schema.generated'
import { computed, ref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsTableRow from '../SettingsTable/SettingsTableRow.vue'
import SettingsTable from '../SettingsTable/SettingsTable.vue'
import CautionAlert from './CautionAlert.vue'
import ContractEditorCreditCardRow from './ContractEditorCreditCardRow.vue'
import { type SettingsPageContractViewFragment } from './SettingsPageContractView.generated'

const props = defineProps<{
  contract: SettingsPageContractViewFragment
}>()

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const isEditing = ref(false)

const validateAndRedirect = async () => {
  if (
    props.contract.paymentMethod !== PaymentMethod.Stripe ||
    props.contract.contractType !== ContractType.Individual
  ) {
    // eslint-disable-next-line fp/no-mutating-methods
    await router.push({ name: 'errorForbidden' })
  }
}

void validateAndRedirect()

const title = computed(() => (isEditing.value ? '請求先 | 編集中' : '請求先'))

const isEmailValid = computed(() => {
  const email = formValues.value.email
  const reg = /.+@.+\..+/
  return reg.test(email)
})

const isButtonDisabled = computed(() => {
  return (
    formValues.value.invoiceCustomerName === '' ||
    formValues.value.email === '' ||
    isEmailValid.value === false ||
    props.contract.isEditablePeriod === false
  )
})

// 半角スペース or 全角スペース + 様 or 御中、を抜き出す
const toTitle = computed(() => {
  const reg = /[\s|　]?(様|御中)$/
  const a = props.contract.invoiceCustomerName.match(reg)
  if (!!a) {
    return a[0]
  }
  return ''
})

const formValues = ref({
  invoiceCustomerName: props.contract.invoiceCustomerName.replace(
    toTitle.value,
    ''
  ),
  email: props.contract.email,
})

const { updateContract } = useUpdateContract()

onBeforeRouteLeave((to, from, next) => {
  if (!isEditing.value) {
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => next(false),
  })
})

const handleButtonClick = async () => {
  if (!isEditing.value) {
    isEditing.value = true
    return
  }
  await updateContract({
    invoiceCustomerName: formValues.value.invoiceCustomerName + toTitle.value,
    email: formValues.value.email,
  })
  isEditing.value = false
}

const handleCancelButtonClick = () => {
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isEditing.value = false
    },
    onAbort: () => {},
  })

  formValues.value.invoiceCustomerName =
    props.contract.invoiceCustomerName.replace(toTitle.value, '')
  formValues.value.email = props.contract.email
}
</script>

<style lang="scss" module>
.content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.poptip {
  margin-left: 8px;

  &__tips {
    width: 120px;
  }
}

.buttonGroup {
  display: flex;
  gap: 16px;
}
</style>
